// src/pages/ProposalsPage.js
// v3.5.81: Admin list view of proposals with status, view counts, approval state.
//
// Mirrors QuotesPage table pattern (alternating row colors, hover, status pill,
// click-to-navigate). The "Open Public URL" action is the primary affordance —
// reps need to share the URL with prospects, and copying it from this list
// is the canonical place to do that. Admins see all proposals; reps see all
// they prepared (RLS handles the permission boundary; the page just renders
// what comes back).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { v3 } from '../lib/styleTokens';
import { Pill } from '../components/v3';

// v3.6.10: status labels for the filter dropdown + pills. Colors now come from
// the v3 Pill (which mirrors statusPill() — identical hexes to the rest of app).
const STATUS_LABEL = {
  draft:           'Draft',
  sent_for_review: 'Sent for Review',
  viewed:          'Viewed',
  approved:        'Approved',
  declined:        'Declined',
  expired:         'Expired',
  superseded:      'Superseded',
};

export default function ProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [status,    setStatus]    = useState('');
  const [copyMsg,   setCopyMsg]   = useState({ id: null, msg: '' });
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    let q = supabase
      .from('proposals')
      // v3.5.91.2: include quote_type in the join — used by navigateToQuote()
      // below to route correctly. Was prefix-matching package_name before,
      // which broke after the v3.5.91.2 prefix-removal migration.
      .select('*, quote:quote_id (id, quote_number, client_name, package_name, quote_type, totals, status)')
      .order('sent_at', { ascending: false })
      .limit(100);
    if (status) q = q.eq('status', status);
    const { data } = await q;
    let rows = data || [];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(p =>
        (p.quote?.client_name || '').toLowerCase().includes(s) ||
        (p.quote?.quote_number || '').toLowerCase().includes(s) ||
        (p.approver_name || '').toLowerCase().includes(s) ||
        (p.approver_email || '').toLowerCase().includes(s)
      );
    }
    setProposals(rows);
    setLoading(false);
  }
  useEffect(() => { load(); }, [search, status]);

  function publicUrl(token) {
    return `${window.location.origin}/.netlify/functions/proposal?token=${token}`;
  }

  async function copyUrl(p) {
    try {
      await navigator.clipboard.writeText(publicUrl(p.public_token));
      setCopyMsg({ id: p.id, msg: 'Copied!' });
      setTimeout(() => setCopyMsg({ id: null, msg: '' }), 1800);
    } catch {
      setCopyMsg({ id: p.id, msg: 'Copy failed' });
      setTimeout(() => setCopyMsg({ id: null, msg: '' }), 2200);
    }
  }

  function navigateToQuote(p) {
    const q = p.quote;
    if (!q) return;
    // v3.5.91.2: route on quote_type column rather than package_name prefix.
    // This was a missed reader during the v3.5.91.1 reader migration —
    // QuotesPage and quotePermissions migrated then; ProposalsPage caught
    // up here. Same migration pattern but no defensive fallback because
    // by v3.5.91.2 the package_name prefix is gone from stored data and
    // the CHECK constraint on quote_type guarantees a valid value.
    const qt = q.quote_type || 'managed_it';
    const path = qt === 'flexit'    ? `/flexIT/${q.id}`
              : qt === 'multisite'  ? `/multisite/${q.id}`
              : qt === 'bundle'     ? `/bundle/${q.id}`
              : qt === 'voice'      ? `/voice/${q.id}`
              :                       `/quotes/${q.id}`;
    navigate(path);
  }

  const th = { padding: v3.space.cellPad, textAlign: 'left', color: v3.color.muted, fontWeight: v3.font.weight.semibold, borderBottom: `1px solid ${v3.color.border}`, fontSize: v3.font.size.micro, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase, whiteSpace: 'nowrap' };
  const td = { padding: v3.space.cellPad, color: v3.color.bodySoft, fontSize: v3.font.size.body };
  const dash = <span style={{ color: v3.color.faint }}>—</span>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: v3.color.bg, fontFamily: v3.font.family }}>
      <div style={{ flex: 1, overflow: 'auto', padding: v3.layout.pagePadding }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: v3.font.size.pageTitle, fontWeight: v3.font.weight.semibold, color: v3.color.body, margin: 0, letterSpacing: v3.font.letterSpacing.tight }}>Proposals</h2>
            <p style={{ fontSize: v3.font.size.label, color: v3.color.muted, marginTop: 4 }}>Client-facing proposals — sent, viewed, and approved</p>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by client, quote #, approver..."
            style={{ flex: 1, padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', color: v3.color.body }} />
          <select value={status} onChange={e => setStatus(e.target.value)}
            style={{ padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, background: v3.color.card, color: v3.color.body }}>
            <option value="">All statuses</option>
            {Object.keys(STATUS_LABEL).map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 20, color: v3.color.muted, fontSize: v3.font.size.body }}>Loading...</div>
        ) : (
          <div style={{ background: v3.color.card, borderRadius: v3.radius.cardLarge, border: `1px solid ${v3.color.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: v3.font.size.body }}>
              <thead>
                <tr style={{ background: v3.color.cardAlt }}>
                  {['Client', 'Quote #', 'Package', 'Status', 'Sent', 'Viewed', 'Approved', 'Approver', 'Actions'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {proposals.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: v3.color.mutedSoft, fontSize: v3.font.size.body }}>No proposals yet. Send your first one from a quote.</td></tr>
                )}
                {proposals.map((p, i) => {
                  const q  = p.quote || {};
                  return (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${v3.color.borderSoft}`, background: i % 2 === 0 ? v3.color.card : v3.color.cardAlt }}>
                      <td style={{ ...td, fontWeight: v3.font.weight.semibold, color: v3.color.body, cursor: 'pointer' }} onClick={() => navigateToQuote(p)}>
                        {q.client_name || '(unknown)'}
                      </td>
                      <td style={{ ...td, fontFamily: v3.font.mono, fontSize: v3.font.size.small, color: v3.color.ink, fontWeight: v3.font.weight.semibold }}>
                        {q.quote_number || '—'}
                      </td>
                      <td style={td}>{q.package_name || '—'}</td>
                      <td style={td}>
                        <Pill variant={p.status}>{STATUS_LABEL[p.status] || 'Draft'}</Pill>
                      </td>
                      <td style={{ ...td, color: v3.color.muted, fontSize: v3.font.size.small }}>
                        {p.sent_at ? new Date(p.sent_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ ...td, color: v3.color.muted, fontSize: v3.font.size.small }}>
                        {p.last_viewed_at
                          ? `${new Date(p.last_viewed_at).toLocaleDateString()} (${p.view_count || 0}×)`
                          : dash}
                      </td>
                      <td style={{ ...td, color: v3.color.muted, fontSize: v3.font.size.small }}>
                        {p.approved_at ? new Date(p.approved_at).toLocaleDateString() : dash}
                      </td>
                      <td style={{ ...td, fontSize: v3.font.size.small }}>
                        {p.approver_name || dash}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button onClick={() => copyUrl(p)}
                            style={{ padding: '4px 10px', fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, color: v3.color.ink, background: v3.color.inkBg, border: `1px solid ${v3.color.inkBorder}`, borderRadius: v3.radius.input, cursor: 'pointer' }}>
                            {copyMsg.id === p.id ? copyMsg.msg : 'Copy URL'}
                          </button>
                          <a href={publicUrl(p.public_token)} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '4px 10px', fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, color: v3.color.body, background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, textDecoration: 'none' }}>
                            ↗ Open
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
